<template>
  <v-card flat class="pa-3 hyp-risk-pulse-ratings">
    <div v-show="!riskPluseRatingsExpanded && !showDetailsOnly">
      <div v-if="showTitle" class="mb-3">
        <span class="details-heading application-heading mr-3"
          ><span v-if="!hasEssentials && !hidePulse">Pulse AI and </span>Risk
          Ratings</span
        >
      </div>
      <div>
        <RisksPulse
          class="ml-n5 hyp-risk-pulse-container"
          v-if="
            selectedLocation &&
            !riskPluseRatingsExpanded &&
            !hasEssentials &&
            !hidePulse
          "
          :selected-location="selectedLocation"
          :show-title="false"
        ></RisksPulse>

        <div
          v-if="risks && !loadingRisks && !selectedPrimaryRisk"
          class="hyp-primary-risk-container"
        >
          <div class="well hyp-risk-grid-2">
            <div
              v-for="(risk, index) in risks"
              :key="risk.id + index"
              class="my-1 hyp-risk-container"
            >
              <div :style="getRiskStyle(risk, true)">
                <RiskRatingDefinition
                  :riskCategoryInfo="getRiskCategoryInfo(risk)"
                >
                  <div
                    :style="getRiskStyle(risk)"
                    class="hyp-risk-icon cursor-pointer pull-left mt-1 mx-1"
                  ></div>
                </RiskRatingDefinition>
                <div class="hyp-risk-type pull-left">{{ risk.type }}:</div>
                <div class="pull-left pr-3 pl-1">{{ getRiskLevel(risk) }}</div>
                <div class="clearfix"></div>
              </div>
            </div>
          </div>
        </div>

        <RiskRatingSub
          v-if="ratings && !loadingRisks && selectedPrimaryRisk"
          :ratings="ratings"
          :selectedPrimaryRisk="selectedPrimaryRisk"
          :show-title="false"
          :show-grid-view="true"
          :params="params"
        ></RiskRatingSub>
      </div>
    </div>
    <div v-show="riskPluseRatingsExpanded || showDetailsOnly">
      <div>
        <span class="details-heading application-heading mr-3"
          >Risk Ratings</span
        >
        <PulseAndRiskDefinitions
          :rating-id="selectedRatingId"
          :initial="'RR'"
          :params="params"
        ></PulseAndRiskDefinitions>
        <div class="pa-0">
          <v-checkbox
            label="SHOW SUBRISKS"
            v-model="showSubrisks"
            hide-details
            class="shrink mt-2 mr-3 pull-left hyp-risk-ratings-checkbox"
            dense
            @click="handleShowSubrisksChange"
          ></v-checkbox>
          <v-checkbox
            label="SHOW ZONES"
            v-model="showZones"
            hide-details
            class="shrink mt-2 mr-3 pull-left hyp-risk-ratings-checkbox"
            dense
            @click="handleShowZonesChange"
          ></v-checkbox>
          <div class="clearfix"></div>
        </div>
      </div>
      <div class="mt-6">
        <div class="hyp-risk-ratings hyp-risk-ratings-grid-5">
          <v-card
            v-for="(selectedRisk, index) in ratings"
            :key="'subrisk' + index"
            :class="{ 'hyp-hide': selectedRisk.riskCategoryParentName }"
            class="mx-2 pa-2"
            :flat="!showSubrisks"
          >
            <RiskRatingSub
              v-if="!selectedRisk.riskCategoryParentName"
              :ratings="ratings"
              :selectedPrimaryRisk="selectedRisk"
              :show-title="false"
              :show-subrisks="showSubrisks"
              :params="params"
            ></RiskRatingSub>
          </v-card>
        </div>
        <div v-if="showZones">
          <div
            class="mt-6"
            v-for="(zone, index) in zones"
            :key="'zonerisk' + index"
          >
            <v-row v-if="zone.properties.ratings"
              ><v-col cols="12">
                <span
                  class="details-heading application-heading pull-left mr-2 mb-2"
                  >{{ "Zone: " + zone.properties.name }}</span
                >
                <PulseAndRiskDefinitions
                  :rating-id="selectedRatingId"
                  :initial="'RR'"
                  class="pull-left"
                  :params="params"
                ></PulseAndRiskDefinitions>
                <div class="clearfix"></div> </v-col
            ></v-row>
            <div class="hyp-risk-ratings hyp-risk-ratings-grid-5">
              <v-card
                v-for="(selectedZoneRisk, index) in zone.properties.ratings"
                :key="'zonesubrisk' + index"
                :class="{
                  'hyp-hide': selectedZoneRisk.riskCategoryParentName,
                }"
                class="mx-2 pa-2"
                :flat="!showSubrisks"
              >
                <RiskRatingSub
                  v-if="!selectedZoneRisk.riskCategoryParentName"
                  :ratings="zone.properties.ratings"
                  :parentRatings="ratings"
                  :selectedPrimaryRisk="selectedZoneRisk"
                  :show-title="false"
                  :show-subrisks="showSubrisks"
                  :params="params"
                ></RiskRatingSub>
              </v-card>
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import lodashGet from "lodash/get";
import lodashFind from "lodash/find";
import lodashRemove from "lodash/remove";
import lodashOrderBy from "lodash/orderBy";
import camelCase from "lodash/camelCase";
import lodashForEach from "lodash/forEach";

import { RISK_LEVELS } from "@/common/constants/controlRisks/risks";

import {
  getRiskRatingCategories,
  getRiskRatingsByWaterCode,
  getRiskRatingsByCountryCode,
} from "@/services/controlRisks/risks.service";
import { hasEssentialsAccess } from "@/services/user/user.service";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";

export default {
  name: "RisksPulseRatings",
  components: {
    RiskRatingSub: () =>
      import("@/components/controlRisks/risks/RiskRatingSub"),
    RisksPulse: () => import("@/components/controlRisks/risks/RisksPulse"),
    PulseAndRiskDefinitions: () =>
      import("@/components/grids/PulseAndRiskDefinitions"),
    RiskRatingDefinition: () =>
      import("@/components/controlRisks/risks/RiskRatingDefinition"),
  },
  props: {
    selectedLocation: {
      type: Object,
      required: true,
    },
    selectedPrimaryRiskCategory: {
      type: Object,
      required: false,
    },
    showDetailsOnly: {
      type: Boolean,
      default: false,
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    risksContentExpanded: {
      type: String,
      required: false,
    },
    params: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  data() {
    const hidePulse = lodashGet(this.params, "hidePulse")
      ? lodashGet(this.params, "hidePulse")
      : false;

    return {
      hasEssentials: hasEssentialsAccess(),
      riskTypes: null,
      riskTypesPrimary: null,
      risks: null,
      ratings: null,
      zones: null,
      selectedPrimaryRisk: null,
      loadingRisks: false,
      selectedRatingId: null,
      riskPluseRatingsExpanded: false,
      showSubrisks: true,
      showZones: true,
      hidePulse: hidePulse,
    };
  },
  methods: {
    async getRatings() {
      this.riskTypesPrimary = await this.getRiskTypesPrimary();
      this.loadingRisks = true;
      this.ratings = null;
      this.zones = null;

      if (
        this.selectedLocation?.type === "water" &&
        this.selectedLocation?.code
      ) {
        const { waters } = await getRiskRatingsByWaterCode(
          this.selectedLocation.code
        );
        if (
          waters &&
          Array.isArray(waters) &&
          waters.length &&
          lodashGet(waters, "[0].properties.ratings")
        ) {
          this.ratings = waters[0].properties.ratings;
        }
      } else {
        const { countries, zones } = await getRiskRatingsByCountryCode(
          this.selectedLocation.iso2Code
        );
        if (
          countries &&
          Array.isArray(countries) &&
          countries.length &&
          lodashGet(countries, "[0].properties.ratings")
        ) {
          this.ratings = countries[0].properties.ratings;
        }
        if (zones && Array.isArray(zones) && zones.length) {
          this.zones = zones;

          this.zones.forEach((zone) => {
            lodashRemove(zone.properties.ratings, (r) => {
              return !(
                lodashFind(this.riskTypesPrimary, ["id", r.riskCategoryId]) ||
                lodashFind(this.riskTypesPrimary, [
                  "id",
                  r.riskCategoryParentId,
                ])
              );
            });
            lodashForEach(zone.properties.ratings, (r) => {
              let rating = r.riskCategoryParentName
                ? lodashFind(this.riskTypesPrimary, {
                    id: r.riskCategoryParentId,
                  })
                : lodashFind(this.riskTypesPrimary, { id: r.riskCategoryId });
              if (rating) {
                r.order = rating.order;
              }
            });
            zone.properties.ratings = lodashOrderBy(
              zone.properties.ratings,
              ["order"],
              ["asc"]
            );
          });
        }
      }

      if (this.ratings) {
        if (this.ratings && Array.isArray(this.ratings)) {
          lodashRemove(this.ratings, (r) => {
            return !(
              lodashFind(this.riskTypesPrimary, ["id", r.riskCategoryId]) ||
              lodashFind(this.riskTypesPrimary, ["id", r.riskCategoryParentId])
            );
          });

          lodashForEach(this.ratings, (r) => {
            let rating = r.riskCategoryParentName
              ? lodashFind(this.riskTypesPrimary, {
                  id: r.riskCategoryParentId,
                })
              : lodashFind(this.riskTypesPrimary, { id: r.riskCategoryId });
            if (rating) {
              r.order = rating.order;
            }
          });
          this.ratings = lodashOrderBy(this.ratings, ["order"], ["asc"]);

          this.risks = [];
          for (let i = 0; i < this.ratings.length; i++) {
            const rating = this.ratings[i];
            if (
              lodashGet(rating, "riskCategoryId") &&
              lodashGet(rating, "rateValue")
            ) {
              this.riskTypesPrimary.forEach((riskType) => {
                if (rating.riskCategoryId === riskType.id) {
                  this.risks.push({
                    id: riskType.id,
                    type: riskType.name,
                    value: rating.rateValue,
                    order: riskType.order,
                  });
                }
              });
            }
          }
          this.risks = lodashOrderBy(this.risks, ["order"], ["asc"]);
        }
      }
      this.loadingRisks = false;
    },
    async getRiskTypesPrimary() {
      this.riskTypes = await this.getFilteredRiskTypes();
      if (this.riskTypes && Array.isArray(this.riskTypes)) {
        let rTypes = [];
        this.riskTypes.forEach((rt) => {
          if (!rt.parentId) {
            rTypes.push(rt);
          }
        });
        return rTypes;
      }
      return null;
    },
    async getFilteredRiskTypes() {
      let riskRatingCategories = await getRiskRatingCategories();
      this.selectedRatingId =
        riskRatingCategories && riskRatingCategories.length > 0
          ? riskRatingCategories[0].id
          : null;
      return riskRatingCategories;
    },
    getRiskLevel(risk) {
      if (risk && risk.value) {
        const rating = lodashFind(RISK_LEVELS, ["value", risk.value]);
        return lodashGet(rating, "name");
      }
      return null;
    },
    getRiskStyle(risk, boxStyle) {
      let isSameAsParent = false;
      const id = risk.id;
      const level = risk.value;
      for (
        let i = 0;
        this.parentRatings && i < this.parentRatings.length;
        i++
      ) {
        const checkLevel = this.parentRatings[i].rateValue;
        const checkId = this.parentRatings[i].riskCategoryId;
        if (id === checkId && level === checkLevel) {
          isSameAsParent = true;
        }
      }
      if (risk && risk.value) {
        const rating = lodashFind(RISK_LEVELS, ["value", risk.value]);
        const color = isSameAsParent ? "#CBCBCB" : lodashGet(rating, "bgColor");

        if (boxStyle) {
          return {};
        }
        return {
          "background-color": color,
          height: "14px",
          width: "14px",
        };
      }
      return null;
    },
    getRiskCategoryInfo(risk) {
      return {
        riskCategoryId: risk.id,
        riskValue: risk.value,
        riskCategoryNameDisplay: camelCase(risk.type),
      };
    },
    handleShowSubrisksChange() {
      if (this.showSubrisks) {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.control_risk_risk_ratings_subrisk,
          EVENT_ACTIONS.selected,
          "Country ratings subrisks - selected"
        );
      } else {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.control_risk_risk_ratings_subrisk,
          EVENT_ACTIONS.unselected,
          "Country ratings subrisks - unselected"
        );
      }
    },
    handleShowZonesChange() {
      if (this.showZones) {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.control_risk_risk_ratings_zone,
          EVENT_ACTIONS.selected,
          "Country ratings zones - selected"
        );
      } else {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.control_risk_risk_ratings_zone,
          EVENT_ACTIONS.unselected,
          "Country ratings zones - unselected"
        );
      }
    },
  },
  watch: {
    selectedLocation: {
      handler() {
        if (
          this.selectedLocation &&
          (this.selectedLocation.iso2Code ||
            (this.selectedLocation.type === "water" &&
              this.selectedLocation.code))
        ) {
          this.selectedPrimaryRisk = null;
          this.getRatings();
        }
      },
      immediate: true,
      deep: true,
    },
    selectedPrimaryRiskCategory: {
      handler() {
        this.selectedPrimaryRisk = this.selectedPrimaryRiskCategory;
      },
      immediate: true,
      deep: true,
    },
    risksContentExpanded: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.riskPluseRatingsExpanded = this.risksContentExpanded
            ? true
            : false;

          if (this.riskPluseRatingsExpanded) {
            this.handlePageTrackerEvent(
              EVENT_CATEGORIES.control_risk_risk_ratings_expanded,
              EVENT_ACTIONS.clicked,
              "Risk Ratings - expanded"
            );
          }
        }
      },
      immediate: true,
      deep: true,
    },
  },
  beforeDestroy() {
    this.selectedPrimaryRisk = null;
  },
};
</script>

<style>
.zones-list .v-chip__content {
  max-width: 380px !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}
.zones-list .v-text-field__details {
  display: none !important;
}
.hyp-hide {
  display: none !important;
}
.hyp-risk-ratings-checkbox .v-label {
  font-size: 14px !important;
}
</style>

<style scoped>
.details-heading {
  font-size: 20px;
}
.content-container {
  max-width: 550px !important;
  font-size: 14px;
}
.hyp-risk-ratings {
  display: grid;
}

.hyp-risk-ratings-grid-4 {
  grid-template-columns: 25% 25% 25% 25%;
}
.hyp-risk-ratings-grid-5 {
  grid-template-columns: 20% 20% 20% 20% 20%;
}

.hyp-risk-icon {
  width: 100%;
  border-radius: 10px;
}
.hyp-risk-type {
  text-transform: capitalize;
}
.hyp-risk-grid-2 {
  display: grid;
  grid-template-columns: 48% 48%;
  grid-column-gap: 4%;
}
.hyp-risk-pulse-container {
  min-height: 10px;
}
</style>
